// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '@environment';
import { KeyProject, HealthScore, ProjectDetailResponse } from '@lfx/key-projects/key-projects.model';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';
import { API_CONSTANTS } from '@lfx/key-projects/key-projects.constants';

/**
 * API service for project operations
 * Single Responsibility: Handle project HTTP operations only
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectApiService {
  private readonly http = inject(HttpClient);
  private readonly cdpUrl = `${environment.apiUrl}/cdp`;
  private readonly _lastProjectDetails = signal<ProjectDetailResponse | null>(null);
  readonly lastProjectDetails = this._lastProjectDetails.asReadonly();

  /**
   * Search projects for typeahead functionality
   */
  searchProjects(searchTerm: string): Observable<
    {
      id: string;
      name: string;
      logo?: string;
      slug?: string;
      description?: string;
      healthScore?: HealthScore;
    }[]
  > {
    if (
      !searchTerm ||
      searchTerm.trim() === '' ||
      searchTerm.trim().length < API_CONSTANTS.SEARCH_MIN_LENGTH
    ) {
      return of([]);
    }

    const params = new HttpParams()
      .set('query', searchTerm.trim())
      .set('pageSize', API_CONSTANTS.AUTOCOMPLETE_PAGE_SIZE.toString());

    return this.http
      .get<{
        data: unknown[];
        total: number;
      }>(`${this.cdpUrl}/projects/search`, { params })
      .pipe(
        map(response => {
          return KeyProjectsHelper.transformBackendProjectsToAutocomplete(response.data);
        }),
        catchError(() => of([]))
      );
  }

  /**
   * Get project details by slug
   */
  getProjectDetails(slug: string): Observable<ProjectDetailResponse> {
    return this.http.get<ProjectDetailResponse>(`${this.cdpUrl}/project/${slug}`).pipe(
      map(response => ({
        ...response,
        slug,
      })),
      tap(response => {
        this._lastProjectDetails.set(response);
      })
    );
  }

  /**
   * Load projects for a specific workspace by project slugs
   */
  loadProjectsByIds(accountId: string, projectSlugs: string[], throwOnError = false): Observable<KeyProject[]> {
    if (projectSlugs.length === 0) {
      return of([]);
    }

    const keyProjectsUrl = `${environment.apiUrl}/key-projects`;
    const params = new HttpParams()
      .set('accountId', accountId)
      .set('projectIds', projectSlugs.join(','));

    return this.http
      .get<{
        keyProjects: KeyProject[];
        orgId: string;
        projectSlugs: string[];
      }>(keyProjectsUrl, { params })
      .pipe(
        map(response => response?.keyProjects || []),
        catchError((err) => {
          if (throwOnError) {
            return throwError(() => err);
          }
          return of([]);
        })
      );
  }

  /**
   * Get count of LF and non-LF projects with activities
   * Used for Home page display
   */
  getLfProjectCounts(
    accountId: string
  ): Observable<{ lfProjectCount: number; nonLfProjectCount: number }> {
    const url = `${environment.apiUrl}/key-projects/project-counts`;
    const params = new HttpParams().set('accountId', accountId);

    return this.http
      .get<{ lfProjectCount: number; nonLfProjectCount: number }>(url, { params })
      .pipe(catchError(() => of({ lfProjectCount: 0, nonLfProjectCount: 0 })));
  }
}

